import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";
import { showSuccessSnackbar, showErrorSnackbar } from "../../redux/slices/snackbar";

import {
  Alert as MuiAlert,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  TextField as MuiTextField,
  Grid,
  CardHeader,
  Typography,
  MenuItem,
} from "@mui/material";
import { spacing } from "@mui/system";
import { useForm, FormProvider } from "react-hook-form";

import SaveCancel from "../../components/SaveCancel";
import useAgreementTracker from "../../hooks/useAgreementTracker";
import AgreementTrackerFields from "./components/AgreementTrackerFields";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Card = styled(MuiCard)(spacing);

// Moved outside to prevent re-creation on render
const AgreementTrackerBreadcrumbs = () => (
  <Breadcrumbs aria-label="Breadcrumb" mt={2}>
    <Typography>Agreement Tracker</Typography>
    <Typography>Add</Typography>
  </Breadcrumbs>
);

const initialValues = {
  tenant: null,
  dateOfAgreementStr: "",
  dateOfAgreement: null,
};

const validationSchema = Yup.object().shape({
  tenant: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .nullable()
    .required("Tenant is required"),
  dateOfAgreement: Yup.string().required("Required"),
});

function AddAgreementTracker() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { addAgreementTracker, isSubmitting } = useAgreementTracker();

  const methods = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const { reset, handleSubmit } = methods;

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        tenant: {
          tenantId: data.tenant?.value,
          tenantName: data.tenant?.label,
        },
      };

      await addAgreementTracker(payload);
      dispatch(showSuccessSnackbar("Agreement Tracker added Generated successfully!"));
      navigate("/agreement-tracker/list");
      reset();
    } catch (err) {
      dispatch(showErrorSnackbar("Failed to generate Agreement Tracker"));
    }
  };

  const cancelBtn = () => {
    navigate("/agreement-tracker/list");
  };

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <Card mb={6}>
          <CardHeader
            title={t("add_agreement_tracker")}
            subheader={<AgreementTrackerBreadcrumbs />}
          />
          <CardContent>
            {isSubmitting ? (
              <Box display="flex" justifyContent="center" my={6}>
                <CircularProgress />
              </Box>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <AgreementTrackerFields fields={validationSchema.fields} />
                <SaveCancel cancelBtn={cancelBtn} resetForm={reset} />
              </form>
            )}
          </CardContent>
        </Card>
      </FormProvider>
    </React.Fragment>
  );
}

export default AddAgreementTracker;
